import React, { useMemo, useState, useEffect, useRef  } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTaskSense } from "../../../../context/TaskSenseContext";
import { useJobCreate } from "../../../../context/JobCreateContext";
import TextareaAutosize from "react-textarea-autosize";
import { ImSpinner2 } from "react-icons/im";
import {
    pickCorrectedText, pickSummary
} from "../../../../util_functions/helpers";


const TaskSenseStepComponent = ({ user }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { refreshDraft } = useJobCreate();

    const { isCrafting, craftJobFromPrompt, error: taskSenseError, lastPayload, clientTimeZone, clientToday } =
        useTaskSense();

    const [aiInput, setAiInput] = useState("");
    const [aiError, setAiError] = useState("");
    const [aiResult, setAiResult] = useState(null);

    const resultRef = useRef(null);

    const TASKSENSE_PLACEHOLDER =
        "Describe your job here including tasks, location, pay, recurrence and schedule.";


    const TASKSENSE_STEPS = [
        { pct: 18, text: "Optimizing Title and Summary…" },
        { pct: 42, text: "Generating Job Title and Summary…" },
        { pct: 65, text: "Refining activity details…" },
        { pct: 70, text: "Final checks…" },
        { pct: 80, text: "Polishing…" },
        { pct: 95, text: "Assembling your Job Draft…" },
    ];

    const useTaskSenseProgress = (isCrafting) => {
        const [progressPct, setProgressPct] = useState(0);
        const [progressText, setProgressText] = useState("");

        useEffect(() => {
            if (!isCrafting) {
                setProgressPct(0);
                setProgressText("");
                return;
            }

            let i = 0;
            setProgressPct(8);
            setProgressText("Starting TaskSense…");

            const timer = setInterval(() => {
                if (i < TASKSENSE_STEPS.length) {
                    setProgressPct(TASKSENSE_STEPS[i].pct);
                    setProgressText(TASKSENSE_STEPS[i].text);
                    i += 1;
                } else {
                    setProgressPct((p) => Math.min(99, p + 1));
                    setProgressText("Almost Done…");
                }
            }, 1200);

            return () => clearInterval(timer);
        }, [isCrafting]);

        const complete = () => {
            setProgressPct(100);
            setProgressText("Done!");
        };

        return { progressPct, progressText, complete };
    };

    const { progressPct, progressText, complete } = useTaskSenseProgress(isCrafting);

    const [placeholder, setPlaceholder] = useState(TASKSENSE_PLACEHOLDER);

    const primaryLocation = useMemo(() => {
        const selected = location?.state?.selectedLocations;
        const suburbValue = location?.state?.suburbValue;

        if (Array.isArray(selected) && selected.length > 0) return selected[0];
        if (suburbValue) return suburbValue;

        return (
            user?.primaryLocation ||
            user?.suburb ||
            user?.city ||
            ""
        );
    }, [location?.state, user]);


    useEffect(() => {
        console.log("[TaskSense UI] aiResult:", aiResult);
    }, [aiResult]);

    useEffect(() => {
    if (aiResult && resultRef.current) {
        resultRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }
}, [aiResult]);

    const handleTaskSenseSubmit = async () => {
        if (!aiInput.trim()) return;

        setAiError("");
        setAiResult(null);

        try {

            const data = await craftJobFromPrompt(
                aiInput.trim(), primaryLocation
            );
            console.log("[TaskSense] API response:", data);

            const refreshed = await refreshDraft();
            console.log("[TaskSense] refreshed draft:", refreshed);

            const correctedText = pickCorrectedText(data, refreshed);
            const formattedSummary = pickSummary(data);

            console.log("[TaskSense] correctedText:", correctedText);
            console.log("[TaskSense] formattedSummary:", formattedSummary);

            setAiResult({ correctedText, formattedSummary });
            complete();
        } catch (err) {
            setAiError(
                err?.message || "Something went wrong while generating your job."
            );
        }
    };

    const handleProceedToReview = async () => {
        await refreshDraft();

        navigate("/account/fs-dashboard/my-jobs/new/standard/step-6", {
            state: { fromTaskSense: true },
        });
    };

    return (
        <div className="tasksense-mode-panel">
            <h3>TaskSense AI Job Crafting</h3>
            <p>
                To get the best result from TaskSense,
                describe your needs including location (E.g. <strong>at home, in our office</strong>), activities, schedule, and pay (E.g. <strong>hourly, daily, yearly</strong>).
            </p>

            <ul className="tasksense-benefits-list">
                <li>Our TaskSense will do the heavy lifting. It'll instantly generate a professional title,
                    structured description, and requirements list based on your sentence.</li>
                <li>Job key details like your preferred dates, times, and pay rates are automatically formatted into a clear, easy-to-read summary.</li>
                <li>Faster job crafting with fewer steps.</li>
                <li>You can still edit any part of the AI-generated draft to ensure it’s exactly right before you post.</li>
            </ul>

            <div className="sample-job-sentence">
                <h3> Sample Job Description</h3>
                <span>I’m looking for someone to help with gardening and housework <strong>at home on Thursday</strong>.
                    I can pay <strong>$18 per hour</strong>, and it should take about <strong>4 hours</strong>,
                    split into two sessions: <strong>11:00 AM – 1:00 PM</strong> and <strong>3:00 PM – 5:00 PM</strong>.</span>
            </div>

            <div className="tasksense-input-wrapper">
                <TextareaAutosize
                    className="tasksense-input"
                    minRows={3}
                    maxRows={8}
                    placeholder={placeholder}
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    onFocus={() => setPlaceholder("")}
                    onBlur={() => {
                        if (!aiInput.trim()) {
                            setPlaceholder(TASKSENSE_PLACEHOLDER);
                        }
                    }}
                    disabled={isCrafting}
                />

                {isCrafting && (

                    <div className="tasksense-progress-wrapper" role="status" aria-live="polite">
                        <div className="tasksense-progress-top">
                            <span className="tasksense-progress-text">{progressText}</span>
                            <span className="tasksense-progress-pct">{progressPct}%</span>
                        </div>

                        <div className="tasksense-progress-bar">
                            <div
                                className="tasksense-progress-bar-fill"
                                style={{ width: `${progressPct}%` }}
                            >
                            </div>
                        </div>
                    </div>
                )}

                <div className="tasksense-actions">
                    <button
                        type="button"
                        className="tasksense-generate-btn"
                        onClick={handleTaskSenseSubmit}
                        disabled={isCrafting || !aiInput.trim()}
                    >
                        {isCrafting ? (
                            <>
                                <ImSpinner2 className="tasksense-btn-spinner" />
                                <span>Generating Job Details ...</span>
                            </>
                        ) : (
                            "Generate with TaskSense"
                        )}
                    </button>
                </div>

                {(aiError || taskSenseError) && (
                    <p className="tasksense-error-text">
                        {aiError || taskSenseError?.message}
                    </p>
                )}
            </div>

            {aiResult && !isCrafting && (
                <div className="tasksense-result" ref={resultRef}>
                    <h3>Corrected Version</h3>
                    <p>{aiResult.correctedText || "-"}</p>

                    <button
                        type="button"
                        className="tasksense-proceed-btn"
                        onClick={handleProceedToReview}
                    >
                        Proceed to Job Review
                    </button>
                </div>
            )}
        </div>
    );
};

export default TaskSenseStepComponent;
